'use client';

// module imports
import { Pencil } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { useDispatch } from 'react-redux';

// file imports
import { updateUser } from '@/redux/slices/authSlice';
import type { AppDispatch } from '@/redux/store';
import { IUser } from '@/types';

export default function ProfileCalendarLinkCard({ user }: { user: IUser }) {
  const dispatch = useDispatch<AppDispatch>();
  const router = useRouter();
  const [editing, setEditing] = useState(false);
  const [calendarLink, setCalendarLink] = useState(user?.calendarLink || '');
  const [saving, setSaving] = useState(false);

  const isStarterPlan = user?.subscription?.plan === 'starter';

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    try {
      // Only send the calendarLink field that was updated
      const updateData = { calendarLink };
      await dispatch(updateUser(updateData)).unwrap();
      setEditing(false);
    } catch (error) {
      console.error('Failed to update calendar link:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setCalendarLink(user?.calendarLink || '');
    setEditing(false);
  };

  const handleUpgrade = () => {
    router.push('/billing');
  };

  return (
    <div className="bg-white rounded-2xl p-4 sm:p-6 md:p-8 mb-6 sm:mb-8 shadow max-w-5xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex-1">
          <h2 className="text-xl sm:text-2xl font-bold text-[#5167b2] mb-2">Calendar Link Options</h2>
          <div className="flex flex-col md:flex-row md:items-center gap-2">
            <span className="text-gray-400 font-medium text-sm sm:text-base mr-2 md:mr-4">Calendar Link</span>
            {isStarterPlan ? (
              <span className="text-sm sm:text-base text-gray-700">Add Your Booking Link to the End of Your TBOS Survey</span>
            ) : (
              <div className="flex-1">
                {editing ? (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                    <input type="url" value={calendarLink} onChange={(e) => setCalendarLink(e.target.value)} className="border-0 border-b border-gray-400 bg-transparent px-0 py-1 text-gray-700 font-medium flex-1 focus:outline-none focus:border-gray-600 text-sm sm:text-base" placeholder="https://calendly.com/your-link" autoFocus />
                    <div className="flex gap-2 sm:gap-4">
                      <button className="text-blue-600 text-xs sm:text-sm font-medium cursor-pointer hover:text-blue-800 transition-colors" onClick={handleSave} disabled={saving}>
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                      <button className="text-blue-600 text-xs sm:text-sm font-medium cursor-pointer hover:text-blue-800 transition-colors" onClick={handleCancel} disabled={saving}>
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                    <span className="text-sm sm:text-base text-gray-700 flex-1">{calendarLink || 'No calendar link added'}</span>
                    <button className="flex items-center gap-1 text-blue-600 text-xs sm:text-sm font-medium cursor-pointer hover:text-blue-800 transition-colors" onClick={() => setEditing(true)}>
                      <Pencil size={12} className="inline-block sm:w-3.5 sm:h-3.5" />
                      Edit
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        {isStarterPlan && (
          <button className="border border-[#5167b2] text-[#5167b2] font-semibold px-4 sm:px-7 py-2 rounded-lg transition hover:bg-[#5167b2]/10 cursor-pointer text-sm sm:text-base mt-4 md:mt-0" onClick={handleUpgrade}>
            Upgrade to Add
          </button>
        )}
      </div>
      <div className="text-pink-500 text-xs sm:text-sm mt-2 text-right">Please Scroll Down to See Elements below</div>
    </div>
  );
}
